// Mac Mini publish test 2026-10-18
// App.js
import React, { useState, useEffect, Suspense, lazy } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation, useParams } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth, database, ref, onValue } from "./firebase";
import { isPortalUser } from "./permissions";

// Eager-loaded (needed on every page load)
import Sidebar from "./Sidebar";
import LogoBar from "./LogoBar";
import AuthPage from "./AuthPage";
import Dashboard from "./Dashboard";
import OpenCloseChecklists from "./OpenCloseChecklists";

import "./App.css";

// Lazy-loaded routes (code-split into separate chunks)
const CalendarMain = lazy(() => import("./CalendarMain"));
const ClassPlannerHome = lazy(() => import("./ClassPlanner/ClassPlannerHome"));
const KravPlanner = lazy(() => import("./KravPlanner/KravPlanner"));
const TaekwondoPlanner = lazy(() => import("./TaekwondoPlanner/TaekwondoPlanner"));
const AnnouncementsPage = lazy(() => import("./ClassPlanner/AnnouncementsPage"));
const PublicAnnouncements = lazy(() => import("./ClassPlanner/PublicAnnouncements"));
const AttendanceScanner = lazy(() => import("./Attendance/AttendanceScanner"));
const AttendanceKiosk = lazy(() => import("./Attendance/AttendanceKiosk"));
const BarcodePrinter = lazy(() => import("./Attendance/BarcodePrinter"));
const ManageTestings = lazy(() => import("./ManageTestings"));
const ManageUsers = lazy(() => import("./ManageUsers"));
const ManageStudents = lazy(() => import("./ManageStudents"));
const StudentProfile = lazy(() => import("./StudentProfile"));
const TournamentsHome = lazy(() => import("./features/tournaments/components/TournamentsHome"));
const ScoringScreen = lazy(() => import("./ScoringScreen"));
const DrillApprovalQueue = lazy(() => import("./ClassPlanner/DrillApprovalQueue"));
const TestingRegistrationPage = lazy(() => import("./TestingRegistrationPage"));
const TestingRegistrationManage = lazy(() => import("./TestingRegistrationManage"));
const TestingRegistrationSuccess = lazy(() => import("./TestingRegistrationSuccess"));
const CouponManager = lazy(() => import("./CouponManager"));
const TagManager = lazy(() => import("./TagManager"));
const ImportContacts = lazy(() => import("./ImportContacts"));
const AtaImport = lazy(() => import("./AtaImport"));
const BlackBeltChecklists = lazy(() => import("./BlackBelt/BlackBeltChecklists"));
const FinancialReport = lazy(() => import("./Reports/FinancialReport"));
const PrivateLessons = lazy(() => import("./PrivateLessons/PrivateLessons"));
const LessonCreditSettings = lazy(() => import("./PrivateLessons/PrivateLessons").then((m) => ({ default: m.LessonCreditSettings })));
const LessonCheckPrompt = lazy(() => import("./PrivateLessons/PrivateLessons").then((m) => ({ default: m.LessonCheckPrompt })));
const BusinessSettings = lazy(() => import("./BusinessSettings"));
const PortalAccounts = lazy(() => import("./PortalAccounts"));
const FamilyConnectionsMigration = lazy(() => import("./FamilyConnectionsMigration"));
const ProShop = lazy(() => import("./proshop/ProShop"));
const CheckoutPagesPage = lazy(() => import("./CheckoutPages/CheckoutPagesPage"));
const CampaignsListPage = lazy(() => import("./Campaigns/CampaignsListPage"));
const CampaignDetailPage = lazy(() => import("./Campaigns/CampaignDetailPage"));
const ManageEvents = lazy(() => import("./ManageEvents"));
const AttendanceHub = lazy(() => import("./Attendance/AttendanceHub"));
const EventRosters = lazy(() => import("./Attendance/EventRosters"));
const RosterDetail = lazy(() => import("./Attendance/RosterDetail"));
const SchedulingHome = lazy(() => import("./Timeclock/SchedulingHome"));
const EventSignupPage = lazy(() => import("./EventSignupPage"));
const EventManageSignup = lazy(() => import("./EventManageSignup"));
const EventVendorView = lazy(() => import("./EventVendorView"));
const PublicCheckoutPage = lazy(() => import("./CheckoutPages/PublicCheckoutPage"));
const CheckoutPageSuccess = lazy(() => import("./CheckoutPages/CheckoutPageSuccess"));
const PaidEventSignupPage = lazy(() => import("./PaidEventSignupPage"));
const PaidEventRegistrationSuccess = lazy(() => import("./PaidEventRegistrationSuccess"));
const PublicInvoicePayment = lazy(() => import("./PublicInvoicePayment"));
const PublicInvoiceSuccess = lazy(() => import("./PublicInvoiceSuccess"));
const EventRegistrationView = lazy(() => import("./EventRegistrationView"));
const EventManagerSignupPage = lazy(() => import("./EventManagerSignupPage"));
const EventManagerSuccess = lazy(() => import("./EventManagerSuccess"));
const PortalLayout = lazy(() => import("./portal/PortalLayout"));
const VideoLibraryAdmin = lazy(() => import("./VideoLibrary/VideoLibraryAdmin"));
const ClassScheduleAdmin = lazy(() => import("./ClassSchedule/ClassScheduleAdmin"));
const AdminMessages = lazy(() => import("./AdminMessages"));
const TextingInbox = lazy(() => import("./Texting/TextingInbox"));
const LeadsBoard = lazy(() => import("./LeadsMaximizer/LeadsBoard"));

// Loading spinner shown while lazy chunks load
const PageLoader = () => (
  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "40vh" }}>
    <div style={{
      width: 36, height: 36, border: "3px solid #e5e7eb",
      borderTopColor: "#2563eb", borderRadius: "50%",
      animation: "spin 0.8s linear infinite"
    }} />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

// Admin portal preview wrapper
function PortalPreviewWrapper({ user, userProfile }) {
  const { studentId } = useParams();
  return <PortalLayout user={user} userProfile={userProfile} adminPreview previewStudentId={studentId} />;
}

function App() {
  const navigate = useNavigate();
  const location = useLocation();

  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(undefined);

  const [cycleId, setCycleId] = useState("");
  const [selectedTestingId, setSelectedTestingId] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Student self check-in kiosk is always locked: no menu, even after a page reload.
  const kioskLocked = location.pathname.startsWith("/attendance/kiosk");
  // Ring scoreboard and display screens are full screen too
  const scoreboardScreen = location.pathname.startsWith("/tournaments/scoreboard");
  const hideChrome = isFullscreen || kioskLocked || scoreboardScreen;
  const [sidebarOpen, setSidebarOpen] = useState(false);

useEffect(() => {
  const handleResize = () => {
    if (window.innerWidth > 1024) {
      setSidebarOpen(false);
    }
  };

  handleResize(); // run once on load
  window.addEventListener("resize", handleResize);

  return () => window.removeEventListener("resize", handleResize);
}, []);

  // ---------------- ROLE DERIVATION ----------------
  const isAdmin = userProfile?.role === "admin" || userProfile?.role === "director" || userProfile?.role === "owner";

  // ---------------- ADMIN ROUTE GUARD ----------------
  const RequireAdmin = ({ children }) => {
    if (!isAdmin) {
      return <Navigate to="/" replace />;
    }
    return children;
  };

  // ---------------- INSTRUCTOR+ ROUTE GUARD ----------------
  const isInstructorOrAbove = isAdmin || userProfile?.role === "instructor";
  const RequireInstructor = ({ children }) => {
    if (!isInstructorOrAbove) {
      return <Navigate to="/" replace />;
    }
    return children;
  };

  // ---------------- AUTH STATE ----------------
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthLoading(false);
    });
    return unsub;
  }, []);

  // ---------------- USER PROFILE ----------------
  useEffect(() => {
    if (!user) {
      setUserProfile(undefined);
      return;
    }

    return onValue(ref(database, `users/${user.uid}`), (snap) => {
      setUserProfile(snap.exists() ? snap.val() : null);
    });
  }, [user]);

  // ---------------- HANDLERS ----------------
  const handleViewTesting = (id) => setSelectedTestingId(id);

  const handleScoreTesting = (id) => {
    setSelectedTestingId(id);
    navigate("/score");
  };

  const judgeDisplay = user?.displayName || user?.email || "Judge";

  // ---------------- PUBLIC ROUTE CHECK (AFTER ALL HOOKS) ----------------
  const isPublicRoute = location.pathname.startsWith('/public') || location.pathname.startsWith('/testing-registration') || location.pathname.startsWith('/event-checkin') || location.pathname.startsWith('/pay');

  if (isPublicRoute) {
    return (
      <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/public/announcements" element={<PublicAnnouncements />} />
        <Route path="/testing-registration/success" element={<TestingRegistrationSuccess />} />
        <Route path="/testing-registration/manage/:registrationId/:signupId/:token" element={<TestingRegistrationManage />} />
        <Route path="/testing-registration/:registrationId" element={<TestingRegistrationPage />} />
        <Route path="/public/event/:eventId" element={<EventSignupPage />} />
        <Route path="/public/event/:eventId/register" element={<PaidEventSignupPage />} />
        <Route path="/public/event/:eventId/registration-success" element={<PaidEventRegistrationSuccess />} />
        <Route path="/public/event/:eventId/manage/:signupId/:token" element={<EventManageSignup />} />
        <Route path="/public/event/:eventId/manage-reg/:regId/:token" element={<EventRegistrationView />} />
        <Route path="/public/event/:eventId/vendor/:vendorCode" element={<EventVendorView />} />
        <Route path="/public/event-manager/:eventId" element={<EventManagerSignupPage />} />
        <Route path="/public/event-manager/:eventId/success" element={<EventManagerSuccess />} />
        <Route path="/event-checkin/:token" element={<EventVendorView />} />
        <Route path="/public/checkout/:pageId" element={<PublicCheckoutPage />} />
        <Route path="/public/checkout/:pageId/success" element={<CheckoutPageSuccess />} />
        <Route path="/pay/invoice/:invoiceId/success" element={<PublicInvoiceSuccess />} />
        <Route path="/pay/invoice/:invoiceId" element={<PublicInvoicePayment />} />
        <Route path="*" element={<Navigate to="/public/announcements" />} />
      </Routes>
      </Suspense>
    );
  }

  // ---------------- LOADING ----------------
  if (authLoading || (user && userProfile === undefined)) {
    return <div style={{ padding: 40 }}>Loading...</div>;
  }

  // ---------------- LOGGED OUT ----------------
  if (!user) {
    return <AuthPage />;
  }

  // ---------------- JUDGE-ONLY LAYOUT ----------------
  if (userProfile?.role === "judge") {
    return (
      <div style={{ minHeight: "100vh", background: "#f5f5f5" }}>
        {/* Judge header */}
        <div style={{
          background: "linear-gradient(135deg, #0f2b5e, #143a7a)",
          color: "#fff",
          padding: "12px 24px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 20, fontWeight: 700 }}>iXL Ninja</span>
            <span style={{
              background: "#f57c00",
              padding: "2px 10px",
              borderRadius: 12,
              fontSize: 12,
              fontWeight: 600
            }}>JUDGE</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <span style={{ fontSize: 14, opacity: 0.8 }}>{judgeDisplay}</span>
            <button
              onClick={() => auth.signOut()}
              style={{
                background: "#dc2626",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                padding: "6px 16px",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer"
              }}
            >
              Logout
            </button>
          </div>
        </div>

        {/* Judge content */}
        <div style={{ maxWidth: 900, margin: "0 auto", padding: 24 }}>
          <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route
              path="/testings"
              element={
                <ManageTestings
                  cycleId={cycleId}
                  setCycleId={setCycleId}
                  selectedTestingId={selectedTestingId}
                  onViewTesting={handleViewTesting}
                  onScoreTesting={handleScoreTesting}
                  isAdmin={false}
                />
              }
            />
            <Route
              path="/score"
              element={
                selectedTestingId ? (
                  <ScoringScreen
                    judgeName={judgeDisplay}
                    testingId={selectedTestingId}
                    cycleId={cycleId}
                    isAdmin={isAdmin}
                    onBack={() => navigate("/testings")}
                  />
                ) : (
                  <Navigate to="/testings" />
                )
              }
            />
            <Route path="*" element={<Navigate to="/testings" />} />
          </Routes>
          </Suspense>
        </div>
      </div>
    );
  }

  // ---------------- STUDENT PORTAL LAYOUT ----------------
  if (isPortalUser(userProfile?.role)) {
    // Gate: require email verification before showing portal
    if (!user.emailVerified) {
      return (
        <div style={{ minHeight: "100vh", background: "#f0f8ff", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div style={{
            background: "#fff",
            borderRadius: 16,
            padding: "40px 36px 36px",
            width: "100%",
            maxWidth: 420,
            boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
            textAlign: "center",
          }}>
            <img src="/ninja-vertical.png" alt="iXL Ninja" style={{ height: 70, objectFit: "contain", marginBottom: 16 }} onError={(e) => { e.target.style.display = "none"; }} />
            <h2 style={{ color: "#0080C6", margin: "0 0 8px", fontSize: 22 }}>Verify Your Email</h2>
            <p style={{ color: "#64748b", fontSize: 14, lineHeight: 1.7, margin: "0 0 24px" }}>
              We've sent a verification link to <strong>{user.email}</strong>. Click the link in your email to activate your portal account.
            </p>
            <p style={{ color: "#94a3b8", fontSize: 13, margin: "0 0 24px" }}>
              After verifying, click the button below to continue.
            </p>
            <p style={{ color: "#94a3b8", fontSize: 12, lineHeight: 1.6, margin: "-12px 0 20px" }}>
              Don't see it? Check your Spam or Junk folder for an email from teamixl@ixlma.com, or ask the front desk to verify your account for you.
            </p>
            <button
              onClick={async () => {
                await user.reload();
                if (user.emailVerified) {
                  window.location.reload();
                } else {
                  alert("Email not verified yet. Please check your inbox and click the verification link.");
                }
              }}
              style={{
                width: "100%",
                padding: "13px 0",
                fontSize: 15,
                fontWeight: 700,
                border: "none",
                borderRadius: 10,
                cursor: "pointer",
                background: "linear-gradient(135deg, #0080C6 0%, #00A8E8 100%)",
                color: "#fff",
                boxShadow: "0 2px 8px rgba(0,128,198,0.35)",
                marginBottom: 12,
              }}
            >
              I've Verified My Email
            </button>
            <button
              onClick={async () => {
                try {
                  const idToken = await user.getIdToken();
                  const res = await fetch("/.netlify/functions/portal-verify-email", {
                    method: "POST", headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ idToken, action: "send", origin: window.location.origin }),
                  });
                  const data = await res.json().catch(() => ({}));
                  if (data.alreadyVerified) { window.location.reload(); return; }
                  if (!res.ok) throw new Error(data.error || "send failed");
                  alert(`Verification email sent to ${user.email}. It comes from teamixl@ixlma.com. If you don't see it in a few minutes, check your Spam or Junk folder.`);
                } catch (e) {
                  try {
                    const { sendEmailVerification } = await import("firebase/auth");
                    await sendEmailVerification(user);
                    alert("Verification email sent! If you don't see it in a few minutes, check your Spam or Junk folder.");
                  } catch (e2) {
                    alert(`Could not send the email right now. Please wait a few minutes and try again, or ask the front desk to verify your account.\n\n(Details for the front desk: ${e?.message || "unknown"} / ${e2?.code || e2?.message || "unknown"})`);
                  }
                }
              }}
              style={{
                width: "100%",
                padding: "11px 0",
                fontSize: 14,
                fontWeight: 600,
                border: "2px solid #e2e8f0",
                borderRadius: 10,
                cursor: "pointer",
                background: "#fff",
                color: "#0080C6",
                marginBottom: 12,
              }}
            >
              Resend Verification Email
            </button>
            <button
              onClick={() => auth.signOut()}
              style={{
                background: "none",
                border: "none",
                color: "#94a3b8",
                cursor: "pointer",
                fontSize: 13,
                fontWeight: 500,
                padding: "8px 0",
                textDecoration: "underline",
              }}
            >
              Sign out
            </button>
          </div>
        </div>
      );
    }

    // Account made with an email that isn't on file: wait for the school to connect it
    if (userProfile?.pendingApproval && !(userProfile.linkedStudents || []).length) {
      return (
        <div style={{ minHeight: "100vh", background: "#f0f8ff", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: "40px 36px 32px", width: "100%", maxWidth: 420, boxShadow: "0 20px 60px rgba(0,0,0,0.15)", textAlign: "center" }}>
            <img src="/ninja-vertical.png" alt="iXL Ninja" style={{ height: 70, objectFit: "contain", marginBottom: 16 }} onError={(e) => { e.target.style.display = "none"; }} />
            <h2 style={{ color: "#0080C6", margin: "0 0 8px", fontSize: 22 }}>Almost there!</h2>
            <p style={{ color: "#64748b", fontSize: 14, lineHeight: 1.7, margin: "0 0 24px" }}>
              Your account is set up, but <strong>{user.email}</strong> isn't on file with us yet. iXL will connect your account to your student profile soon. Once that's done, sign in again and your portal will be ready.
            </p>
            <button onClick={() => window.location.reload()}
              style={{ width: "100%", padding: "13px 0", fontSize: 15, fontWeight: 700, border: "none", borderRadius: 10, cursor: "pointer", background: "linear-gradient(135deg, #0080C6 0%, #00A8E8 100%)", color: "#fff", marginBottom: 12 }}>
              Check again
            </button>
            <button onClick={() => auth.signOut()} style={{ background: "none", border: "none", color: "#94a3b8", cursor: "pointer", fontSize: 13, textDecoration: "underline" }}>
              Sign out
            </button>
          </div>
        </div>
      );
    }

    return (
      <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/portal/*" element={<PortalLayout user={user} userProfile={userProfile} />} />
        <Route path="*" element={<Navigate to="/portal" replace />} />
      </Routes>
      </Suspense>
    );
  }

  // ---------------- LOGGED IN ----------------
  return (
   <div className={`app-layout ${hideChrome ? "fullscreen" : ""}`}>

 {/* HAMBURGER (mobile only via CSS) */}
{!hideChrome && (
  <button
    onClick={() => setSidebarOpen(open => !open)}
    className="mobile-hamburger"
  >
    &#9776;
  </button>
)}

{/* SIDEBAR */}
{!hideChrome && (
  <>
    <Sidebar
      isAdmin={isAdmin}
      userRole={userProfile?.role || "staff"}
      isOpen={sidebarOpen}
      onClose={() => setSidebarOpen(false)}
    />

    {sidebarOpen && (
      <div
        className="sidebar-overlay"
        onClick={() => setSidebarOpen(false)}
      />
    )}
  </>
)}


  <div className="main-area">

        {!hideChrome && <LogoBar />}

        {!kioskLocked && <button
          className="fullscreen-toggle"
          onClick={() => setIsFullscreen((f) => !f)}
          title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
        >
          {isFullscreen ? "\u2715" : "\u26F6"}
        </button>}

        {/* After a private lesson ends, ask whether the student showed */}
        <Suspense fallback={null}><LessonCheckPrompt userRole={userProfile?.role} /></Suspense>

        <div className="admin-main">
          <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/calendar" element={<CalendarMain />} />
            <Route path="/messages" element={<AdminMessages />} />
            <Route path="/texting" element={<TextingInbox />} />
            <Route path="/checklists" element={<OpenCloseChecklists isAdmin={isAdmin} />} />
            <Route path="/leads-max" element={<RequireAdmin><LeadsBoard /></RequireAdmin>} />
            <Route 
              path="/announcements" 
              element={<AnnouncementsPage isAdmin={isAdmin} />} 
            />
            <Route path="/attendance" element={<AttendanceHub />} />
            <Route path="/black-belt/*" element={<BlackBeltChecklists />} />
            <Route path="/reports" element={<RequireAdmin><FinancialReport /></RequireAdmin>} />
            <Route path="/private-lessons" element={<PrivateLessons />} />
            <Route path="/setup/lesson-credits" element={<RequireAdmin><LessonCreditSettings /></RequireAdmin>} />
            <Route path="/attendance/scanner" element={<AttendanceScanner />} />
            <Route path="/attendance/kiosk" element={<AttendanceKiosk />} />
            <Route path="/attendance/barcodes" element={<BarcodePrinter />} />
            <Route
              path="/class-planner"
              element={<ClassPlannerHome isAdmin={isAdmin} />}
            />
            <Route
              path="/krav-planner"
              element={<KravPlanner isAdmin={isAdmin} />}
            />
            <Route
              path="/tkd-planner"
              element={<TaekwondoPlanner isAdmin={isAdmin} />}
            />

            <Route
              path="/video-library"
              element={
                <RequireAdmin>
                  <VideoLibraryAdmin />
                </RequireAdmin>
              }
            />

            <Route
              path="/class-schedule"
              element={
                <RequireAdmin>
                  <ClassScheduleAdmin />
                </RequireAdmin>
              }
            />

            <Route
              path="/testings"
              element={
                <ManageTestings
                  cycleId={cycleId}
                  setCycleId={setCycleId}
                  selectedTestingId={selectedTestingId}
                  onViewTesting={handleViewTesting}
                  onScoreTesting={handleScoreTesting}
                  isAdmin={isAdmin}
                />
              }
            />

            <Route
              path="/score"
              element={
                selectedTestingId ? (
                  <ScoringScreen
                    judgeName={judgeDisplay}
                    testingId={selectedTestingId}
                    cycleId={cycleId}
                    isAdmin={isAdmin}
                    onBack={() => navigate("/testings")}
                  />
                ) : (
                  <Navigate to="/testings" />
                )
              }
            />

            <Route
              path="/students/:id"
              element={
                <RequireInstructor>
                  <StudentProfile />
                </RequireInstructor>
              }
            />

            <Route
              path="/portal-preview/:studentId/*"
              element={
                <RequireAdmin>
                  <PortalPreviewWrapper user={user} userProfile={userProfile} />
                </RequireAdmin>
              }
            />

            <Route
              path="/students"
              element={
                <RequireInstructor>
                  <ManageStudents />
                </RequireInstructor>
              }
            />

            <Route
              path="/users"
              element={
                <RequireAdmin>
                  <ManageUsers />
                </RequireAdmin>
              }
            />

            {/* DRILL APPROVAL QUEUE */}
            <Route
              path="/drill-approvals"
              element={
                <RequireAdmin>
                  <DrillApprovalQueue />
                </RequireAdmin>
              }
            />

            <Route
              path="/tournaments/*"
              element={<TournamentsHome isAdmin={isAdmin} />}
            />

            {/* PRO SHOP */}
            <Route
              path="/pro-shop"
              element={
                <RequireAdmin>
                  <ProShop />
                </RequireAdmin>
              }
            />
            <Route
              path="/checkout-pages"
              element={
                <RequireAdmin>
                  <CheckoutPagesPage />
                </RequireAdmin>
              }
            />
            <Route
              path="/campaigns"
              element={
                <RequireAdmin>
                  <CampaignsListPage />
                </RequireAdmin>
              }
            />
            <Route
              path="/campaigns/:campaignId"
              element={
                <RequireAdmin>
                  <CampaignDetailPage />
                </RequireAdmin>
              }
            />

            {/* SETUP ROUTES */}
            <Route
              path="/setup/business"
              element={
                <RequireAdmin>
                  <BusinessSettings />
                </RequireAdmin>
              }
            />
            <Route
              path="/setup/coupons"
              element={
                <RequireAdmin>
                  <CouponManager />
                </RequireAdmin>
              }
            />
            <Route
              path="/setup/tags"
              element={
                <RequireAdmin>
                  <TagManager />
                </RequireAdmin>
              }
            />
            <Route
              path="/setup/import"
              element={
                <RequireAdmin>
                  <ImportContacts />
                </RequireAdmin>
              }
            />
            <Route
              path="/setup/ata-import"
              element={
                <RequireAdmin>
                  <AtaImport />
                </RequireAdmin>
              }
            />
            <Route
              path="/setup/portal-accounts"
              element={
                <RequireAdmin>
                  <PortalAccounts />
                </RequireAdmin>
              }
            />
            <Route
              path="/setup/family-connections"
              element={
                <RequireAdmin>
                  <FamilyConnectionsMigration />
                </RequireAdmin>
              }
            />

            {/* SCHEDULING */}
            <Route
              path="/scheduling"
              element={
                <SchedulingHome
                  userRole={userProfile?.role || "staff"}
                  currentUserId={user?.uid}
                />
              }
            />

            {/* EVENT ROUTES */}
            <Route
              path="/rosters"
              element={
                <RequireAdmin>
                  <EventRosters />
                </RequireAdmin>
              }
            />
            <Route
              path="/rosters/:rosterId"
              element={
                <RequireAdmin>
                  <RosterDetail />
                </RequireAdmin>
              }
            />
            <Route
              path="/events-manager"
              element={
                <RequireAdmin>
                  <ManageEvents />
                </RequireAdmin>
              }
            />

            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
          </Suspense>
        </div>
      </div>
    </div>
  );
}

export default App;
